"use client";

import * as React from "react";
import {
  Text,
  SectionCard,
  CardTitle,
  CardDescription,
  Section,
  Button,
  Sheet,
  Input,
} from "@/src/components";
import { Label } from "@/src/components/input/label";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsSheet() {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Sheet
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            A panel that slides out from the edge of the screen. Useful for forms,
            settings, or detailed views without leaving the current context.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/sheet" />
        </SectionCard>
      </Section>

      {/* Basic Example */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Basic Sheet</CardTitle>
              <CardDescription>Simple sheet with title and content</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex gap-4">
            <Sheet
              trigger={<Button variant="outline">Open Sheet</Button>}
              title="Sheet Title"
              description="This is a description of the sheet content."
            >
              <div className="space-y-4">
                <Text>This is the sheet content. You can put any content here.</Text>
              </div>
            </Sheet>
          </div>
        </SectionCard>
      </Section>

      {/* With Form */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Sheet with Form</CardTitle>
              <CardDescription>Sheet containing a form with footer actions</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <Sheet
            trigger={<Button variant="outline">Edit Profile</Button>}
            title="Edit Profile"
            description="Make changes to your profile here."
            footer={
              <div className="flex gap-2">
                <Button variant="outline">Cancel</Button>
                <Button>Save Changes</Button>
              </div>
            }
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" placeholder="Enter your name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="Enter your email" />
              </div>
            </div>
          </Sheet>
        </SectionCard>
      </Section>

      {/* Different Sides */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Sheet Positions</CardTitle>
              <CardDescription>Sheets can slide in from any edge</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex flex-wrap gap-4">
            <Sheet
              trigger={<Button variant="outline">Right</Button>}
              side="right"
              title="Right Sheet"
            >
              <Text>This sheet slides in from the right.</Text>
            </Sheet>
            <Sheet
              trigger={<Button variant="outline">Left</Button>}
              side="left"
              title="Left Sheet"
            >
              <Text>This sheet slides in from the left.</Text>
            </Sheet>
            <Sheet
              trigger={<Button variant="outline">Top</Button>}
              side="top"
              title="Top Sheet"
            >
              <Text>This sheet slides in from the top.</Text>
            </Sheet>
            <Sheet
              trigger={<Button variant="outline">Bottom</Button>}
              side="bottom"
              title="Bottom Sheet"
            >
              <Text>This sheet slides in from the bottom.</Text>
            </Sheet>
          </div>
        </SectionCard>
      </Section>

      {/* Controlled */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Controlled Sheet</CardTitle>
              <CardDescription>Control the sheet open state programmatically</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex gap-4 items-center">
            <Button variant="outline" onClick={() => setIsOpen(true)}>
              Open Controlled Sheet
            </Button>
            <Text className="text-sm text-muted-foreground">
              Open: {isOpen ? "Yes" : "No"}
            </Text>
          </div>
          <Sheet
            open={isOpen}
            onOpenChange={setIsOpen}
            title="Controlled Sheet"
            description="This sheet is controlled by state."
            footer={
              <Button onClick={() => setIsOpen(false)}>Close</Button>
            }
          >
            <Text>You can control this sheet programmatically.</Text>
          </Sheet>
        </SectionCard>
      </Section>

      {/* Props Reference */}
      <Section>
        <SectionCard
          header={<CardTitle>Props Reference</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Prop</th>
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">trigger</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 text-muted-foreground">Element that opens the sheet</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">title</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 text-muted-foreground">Sheet title in the header</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">description</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 text-muted-foreground">Description below the title</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">side</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">"top" | "right" | "bottom" | "left"</td>
                <td className="py-2 text-muted-foreground">Edge from which sheet appears (default: "right")</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">children</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 text-muted-foreground">Content in the body</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">footer</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 text-muted-foreground">Footer content (e.g., action buttons)</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">showClose</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 text-muted-foreground">Show close button (default: true)</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">open</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 text-muted-foreground">Controlled open state</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">onOpenChange</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">(open: boolean) =&gt; void</td>
                <td className="py-2 text-muted-foreground">Callback when state changes</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>
    </>
  );
}
